/* global heading */
// See https://github.com/NullVoxPopuli/ember-eslint-parser/issues/29

import writingKs1Available from '@images/writing-ks1-available.png'
import writingStudentMenu from '@images/writing-student-menu.png'
import writingAvatars from '@images/writing-avatars.png'
import writingFeedback from '@images/writing-feedback.png'
import wlLogo from '@images/logos/wl-logo.svg'
import Component from '@glimmer/component'
import { t } from 'ember-intl'
import perform from 'ember-concurrency/helpers/perform'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { WritingPitchLi } from 'teacher-ui-client/components/page/access-missing/writing-pitch-li'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import type { Task } from 'ember-concurrency'
import type SessionService from 'teacher-ui-client/services/session'
import { EMEA } from 'teacher-ui-client/feature-control/marketing-regions'

interface Signature {
  Element: HTMLDivElement
  Args: {
    createTeacherSubTask: Task<void, [SubscriptionType, Teacher]>
  }
}

export class PageAccessMissingWriting extends Component<Signature> {
  @service session!: SessionService

  get isOneRoster() {
    return Boolean(this.session.currentSchool.oneRosterEnabledAt)
  }

  get inEMEA() {
    return EMEA.includes(this.session.currentSchool.countryCode)
  }

  <template>
    <div class="mx-auto max-w-4xl overflow-hidden" ...attributes>
      <section>
        <heading>
          <img class="mx-auto w-64" src={{wlLogo}} alt={{t "products.writing-legends"}} />
          <h3 class="text-wl-blue-300 mt-4 text-center italic">
            {{t "pages.accessMissing.writing.tagline"}}
          </h3>
        </heading>

        {{#unless this.isOneRoster}}
          <div class="mt-8 text-center">
            <UiButton
              data-test-start-trial
              class="mx-auto"
              disabled={{@createTeacherSubTask.isRunning}}
              @appearance={{ButtonAppearance.regular}}
              {{on "click" (perform @createTeacherSubTask SubscriptionType.Writing this.session.currentTeacher)}}
            >
              {{t "pages.accessMissing.writing.button"}}
            </UiButton>
          </div>
        {{/unless}}

        <div class="mt-8 flex text-xl">
          <div>
            <p class="mb-3">{{t "pages.accessMissing.writing.explainer.pre"}}</p>
            <ol class="marker:text-oceany-blue-325 list-inside list-decimal space-y-2 marker:font-bold">
              <li>{{t "pages.accessMissing.writing.explainer.item1"}}</li>
              <li>{{t "pages.accessMissing.writing.explainer.item2"}}</li>
              <li>{{t "pages.accessMissing.writing.explainer.item3"}}</li>
            </ol>
          </div>
          {{#if this.inEMEA}}
            <img
              src={{writingKs1Available}}
              alt={{t "pages.accessMissing.writing.ks1AvailableAlt"}}
              width="151"
              height="154"
            />
          {{/if}}
        </div>
      </section>

      <section>
        <h2 class="text-oceany-blue-325 my-8 text-center">
          {{t "pages.accessMissing.writing.pitch.header"}}
        </h2>

        <div class="grid auto-rows-min grid-cols-2 items-center gap-4 text-xl">
          <iframe
            class="snappy:invisible aspect-video w-full rounded-sm"
            src="https://www.youtube.com/embed/xEqB5UAeUhY"
            title="YouTube video player - Welcome to Writing Legends"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>

          <ul class="space-y-4">
            <WritingPitchLi @text={{t "pages.accessMissing.writing.pitch.item4" htmlSafe=true}} />
            <WritingPitchLi @text={{t "pages.accessMissing.writing.pitch.item5" htmlSafe=true}} />
          </ul>

          <ul class="space-y-4">
            <WritingPitchLi @text={{t "pages.accessMissing.writing.pitch.item1" htmlSafe=true}} />
            <WritingPitchLi @text={{t "pages.accessMissing.writing.pitch.item2" htmlSafe=true}} />
            <WritingPitchLi @text={{t "pages.accessMissing.writing.pitch.item3" htmlSafe=true}} />
          </ul>

          <div>
            <img class="mx-auto" src={{writingAvatars}} alt={{t "pages.accessMissing.writing.avatarsAlt"}} />
            <img class="mx-auto" src={{writingStudentMenu}} alt={{t "pages.accessMissing.writing.menuAlt"}} />
          </div>

          <img class="mx-auto" src={{writingFeedback}} alt={{t "pages.accessMissing.writing.feedbackAlt"}} />

          <ul class="space-y-4">
            <WritingPitchLi @text={{t "pages.accessMissing.writing.pitch.item6" htmlSafe=true}} />
            <WritingPitchLi @text={{t "pages.accessMissing.writing.pitch.item7" htmlSafe=true}} />
          </ul>
        </div>
      </section>

      <div class="my-8 text-center">
        <div class="mb-8 italic">
          {{t "pages.accessMissing.writing.britishEnglishNote"}}
        </div>
        {{#if this.isOneRoster}}
          <UiButtonLinkTo
            data-test-contact-us-button
            class="mx-auto"
            @appearance={{ButtonAppearance.regular}}
            @route="subscription-type.contact-us"
          >
            {{t "pages.accessMissing.writing.contactUs"}}
          </UiButtonLinkTo>
        {{else}}
          <UiButton
            data-test-start-trial
            class="mx-auto"
            disabled={{@createTeacherSubTask.isRunning}}
            @appearance={{ButtonAppearance.regular}}
            {{on "click" (perform @createTeacherSubTask SubscriptionType.Writing this.session.currentTeacher)}}
          >
            {{t "pages.accessMissing.writing.button"}}
          </UiButton>
        {{/if}}
      </div>
    </div>
  </template>
}
